import { SCORING_METHOD } from "@/defines/scoringmethod";
import { LanguageId } from "./languageids";
import { BiddingBoxKind, CardFace, CardOrder, MoveTrumps, PlayBy } from "./constants";
import { ConfigKey, REGISTRY_VERSION, keysForLogin, keysToKeep, keysToLog } from "./configkeys";
import { Version } from "./version";
import { type ConfigFile } from "./configfile";
import { ConfigDefault } from "./configdefaults";
import { useGameServer } from "@/stores/gameserverstore";
import { toRaw } from "vue";
import { isElectron } from "./electron";
import { Device } from "./device";

const memory = window.localStorage;

export function readNumber(key: string, defaultValue: number): number {
    const value = memory.getItem(key);

    if (value != null) {
        const n = Number(value);
        if (Number.isNaN(n)) {
            console.error(`Key ${key} return number NaN`);
        }
        return n;
    }
    return defaultValue;
}
export function readBoolean(key: string, defaultValue: boolean): boolean {
    const value = memory.getItem(key) ?? defaultValue.toString();
    return value === "true";
}

export function readString(key: string, defaultValue: string): string {
    const value = memory.getItem(key);

    if (value !== null) {
        return value;
    }
    return defaultValue;
}
export function writeKey(key: string, value: string | number | boolean) {
    memory.setItem(key, value.toString());
}

/**
 * Stamp the migration marker, raising it only. A client that is rolled back to an older
 * build - staged rollouts do exactly that - would otherwise write its own lower version
 * here, and the newer build would then re-run a migration it had already applied.
 */
function stampMigrationVersion() {
    const stored = readNumber(ConfigKey.KEY_MIGRATION_VERSION, 0);
    const highest = Number.isFinite(stored) ? stored : 0;
    writeKey(ConfigKey.KEY_MIGRATION_VERSION, Math.max(highest, Version.getVersionNumber()));
}

export type LastLogin = {
    name: string,
    date: number
}

export class Config {

    languageId = LanguageId.LANGUAGE_DUTCH;
    cardLanguageId = this.languageId;

    get rowSize() {
        return Device.isTouch && !isElectron() ? "large" : "small";
    }

    get isSingleWindow() {
        return this.singleWindow || !isElectron();
    }

    // How a card is actually played, accounting for the current view. Diagram
    // view has no draggable cards (DGHandCard implements only click and
    // double-click), so a stored "dragging" preference behaves as clicking
    // there. Consumers read this getter; the raw choice lives in `_playBy` and is
    // kept untouched so dragging is restored in card view. The setter writes the
    // raw value, so assigning `config.playBy = ...` stores the real preference.
    get playBy(): PlayBy {
        return this.diagramMode && this._playBy === PlayBy.dragging ? PlayBy.clicking : this._playBy;
    }
    set playBy(value: PlayBy) {
        this._playBy = value;
    }

    get lastLogin(): LastLogin {
        const jsonObj = localStorage.getItem(ConfigKey.KEY_LAST_LOGIN);

        if (jsonObj) {
            try {
                return JSON.parse(jsonObj);
            } catch (e) {
                console.error(e, jsonObj);
            }
        }
        return { name: '', date: 0 };
    }

    setLastLogin(name: string) {
        const last = {
            name: name,
            date: Date.now()
        }
        localStorage.setItem(ConfigKey.KEY_LAST_LOGIN, JSON.stringify(last));
    }

    writeLock = false; // shouldn't write to registry if this is true
    // isMobile;
    // isIPad;
    // isTouch;
    // hasTouchHandler; // iPad / Safari, better use touch handler, not click.
    // isChrome;
    // isIE;
    // isIOS7;
    // isIOS8;
    // isIOS9;
    // isRetina;
    // isApp;

    // STORED prefs + defaults
    backgroundColourLM: string = ConfigDefault.backgroundColourLM;
    backgroundColourDM: string = ConfigDefault.backgroundColourDM;

    fontSize: number = ConfigDefault.fontSize;
    fontBold = true;
    // One step bolder on tablets for better readability on high-DPI screens
    fontWeight: number = (Device.isTouch && Device.isRetina) ? ConfigDefault.fontWeight + 100 : ConfigDefault.fontWeight; // 400-900

    uuid = ""; // used for identifying a device
    lastPlayedScoringMethod = SCORING_METHOD.MATCHPOINTS;

    autoZoom = true;
    autoSplitter = true;
    fourColourDeck = false;

    autoShowChat = true;
    autoShowBoardResult = true;

    soundAtBidTurn = true;
    soundAtPlayTurn = true;
    soundAtPrivateMessages = true;
    soundAtMembersEntry = true;

    diagramMode = false;
    moveTrumps = MoveTrumps.dummy;
    showTrickCounter = window.innerHeight > 720;

    biddingBox = BiddingBoxKind.compact;
    cardOrder = CardOrder.alternating;
    cardFace = CardFace.modern;
    showBackOfCard = true;

    /*
     * Show what a bid means to the bot for every bid it has an agreement for, not
     * only the artificial ones. Natural bids mostly explain themselves, so they are
     * noise while the player is choosing -- but this is a matter of taste, and the
     * data for them arrives either way. Hidden for now: persisted, with no way to
     * reach it from the options dialog yet.
     */
    bidHintsAll = false;
    cardWideSpaced = Device.isMobile;

    hintHowToPlay = true;
    hintPlayConventionCard = true;

    // Raw stored play preference; read/written through the `playBy` accessor.
    _playBy = Device.isMobile ? PlayBy.dragging : PlayBy.clicking

    showActivitiesInLobby = true;
    showActivitiesWithTables = false;

    // video bridge options
    cameraDevice = "Default";
    audioInputDevice = "Default";
    audioOuputDevice = "Default";
    muteCamera = false;
    muteMicrophone = false;

    // playing options
    hideBiddingAfterLead = false;
    defaultPass = false;

    // spectator / vugraph options
    showTricksAfterLead = true;
    dockTricks = false;
    dockBidding = false;
    hideCardsDuringKibitz = false;

    playByKeyboard = false; // can we input bid/play commands by special keys
    autoEnterPBK = false; // an enter is automaticly issued when a valid play command is detected

    animations = true; // show animations with dialogs and other elements
    singleWindow = true; // when running under electron, we can select to view the app with windows

    // always on top
    onTopCC = true;
    onTopContacts = false;
    onTopBoardResults = false;
    onTopCompetitionResults = false;

    // The ?server=<code> selection (e.g. "pong", "galaga"). Empty means the
    // default production conf (public/server.conf) was loaded without a code.
    serverCode = "";

    // Modern faces drawn as SVG or as the old PNGs, a fallback while the SVG cards are new. '' follows
    // the conf key cardsvg (default on) except on Safari before 15; /cards png|svg sets it for one user.
    cardImages: '' | 'svg' | 'png' = '';

    get cardSvg() {
        if (this.cardImages) {
            return this.cardImages === 'svg';
        }
        const safari = Device.safariVersion();
        return this.cardSvgServer && !(safari > 0 && safari < 15);
    }

    url: { [key: string]: string | undefined } = {
        forgotPW: "",
        reactivation: ""
    };

    // NON PERSISTENT members

    // show the dialog message when first alerting while playing with Sabi
    showNoAlertSabi = true;

    // cardsvg from the server conf. Deliberately not stored for sub windows without the conf: they show no
    // cards, and the key only matters on the web (Electron packs its conf), which has no sub windows.
    cardSvgServer = true;

    // a temp account is created externally (by Core), use this.
    temporaryAccount = false;

    // VueUse formatDate() patterns, see DateTimeFormat
    dateFormat = "ddd D-M-YYYY";
    timeFormat = "HH:mm";

    init() {
        // EOD
        // const version = memory.getItem(ConfigKey.KEY_VERSION);
        // if (version === "very old") { // fill in the version
        //     this.write(); // resets default values
        // }
        this.read();
        this.migrate();
    }

    /**
     * One-time settings changes that have to be forced on existing installations.
     *
     * KEY_MIGRATION_VERSION holds the client version that last ran this method, as the
     * sortable number from Version.getVersionNumber() (7.1.35 -> 700010035). A block runs
     * only when that marker is still below its own version, so it fires exactly once per
     * installation, whichever older version the user upgraded from. Blocks of a version
     * newer than the running client are skipped, so a marker written by an older client
     * (a dev build, or a downgrade) doesn't swallow the migration when the release lands.
     *
     * Runs after read(), so it overrides what was stored. Every window calls init(), but
     * they all compute the same result, so a sub window writing the same keys is harmless.
     */
    migrate() {

        if (this.fontSize == 13.0) {
            this.fontSize = ConfigDefault.fontSize;
        }

        // A corrupt marker makes readNumber return NaN, which loses every comparison
        // below: no block would run, yet the marker would be stamped as done. Treat
        // anything unusable as "never migrated" so it heals itself.
        const stored = readNumber(ConfigKey.KEY_MIGRATION_VERSION, 0);
        const migrated = Number.isFinite(stored) ? stored : 0;
        const current = Version.getVersionNumber();

        if (migrated >= current) {
            return;
        }

        const needs = (version: number) => migrated < version && current >= version;

        // 7.1.35: move everyone still on 'all' over to 'dummy', once. Whoever
        // deliberately picked 'none' keeps that choice.
        if (needs(700010035)) {
            if (this.moveTrumps === MoveTrumps.all) {
                this.moveTrumps = MoveTrumps.dummy;
                writeKey(ConfigKey.KEY_MOVE_TRUMPS, this.moveTrumps);
            }
        }

        stampMigrationVersion();
    }

    /**
     * restore to defaults setting.. also clear all windows positions.
     **/
    restoreDefaultSettings() {
        return new Promise<void>((resolve) => {
            const keep = {};

            for (const k of keysToKeep) {
                keep[k] = localStorage.getItem(k);
            };
            // remember all state / server config vars
            for (let i = 0; i < localStorage.length; i++) {
                const k = localStorage.key(i);

                if (k && (k.startsWith('state-') || k.startsWith('server-') || k.startsWith('store-'))) {
                    keep[k] = localStorage.getItem(k);
                }
            }

            localStorage.clear();

            // create new config with default values and write them to local storage.
            new Config().write();

            // write the key kept
            for (const k of Object.keys(keep)) {
                localStorage.setItem(k, keep[k]);
                console.debug('set kept', k, keep[k]);
            };

            // and now read them in.
            this.read();

            resolve();
        });
    }

    toJSON() {
        const obj: any = {};
        for (let key of keysForLogin) {
            const s = memory.getItem(key);

            if (s !== null) {
                obj[key] = s;
            }
        }
        return JSON.stringify(obj);
    }

    toDebugString() {
        let s = "config=" + (Device.isTouch ? " touch" : "") + (Device.isMobile ? " mobile" : "");

        for (let key of keysToLog) {
            s += " / " + key + ": " + (memory.getItem(key) ?? "");
        }

        return s;
    }

    read() {
        this.languageId = readNumber(ConfigKey.KEY_LANGUAGE, this.languageId);
        this.cardLanguageId = readNumber(ConfigKey.KEY_CARD_LANGUAGE, this.cardLanguageId);
        // this.autoLogin = readBoolean(ConfigKey.KEY_AUTO_LOGIN, this.autoLogin);
        this.autoShowChat = readBoolean(ConfigKey.KEY_AUTO_SHOW_CHAT, this.autoShowChat);
        this.autoShowBoardResult = readBoolean(ConfigKey.KEY_AUTO_SHOW_BOARD_RESULTS, this.autoShowBoardResult);
        this.soundAtBidTurn = readBoolean(ConfigKey.KEY_SOUND_BID_TURN, this.soundAtBidTurn);
        this.soundAtPlayTurn = readBoolean(ConfigKey.KEY_SOUND_PLAY_TURN, this.soundAtPlayTurn);
        this.soundAtPrivateMessages = readBoolean(ConfigKey.KEY_SOUND_PRIVATE_MSG, this.soundAtPrivateMessages);
        this.soundAtMembersEntry = readBoolean(ConfigKey.KEY_SOUND_MEMBER_ENTRY, this.soundAtMembersEntry);
        this.biddingBox = readNumber(ConfigKey.KEY_BIDDINGBOX_KIND, this.biddingBox);
        this.cardOrder = readNumber(ConfigKey.KEY_CARD_ORDER, this.cardOrder);
        this.cardFace = readNumber(ConfigKey.KEY_CARD_FACE, this.cardFace);
        this.cardWideSpaced = readBoolean(ConfigKey.KEY_CARD_WIDE_SPACED, this.cardWideSpaced);
        this.cardImages = readString(ConfigKey.KEY_CARD_IMAGES, this.cardImages) as Config['cardImages'];
        this.fourColourDeck = readBoolean(ConfigKey.KEY_FOUR_COLOUR_DECK, this.fourColourDeck);
        this.showBackOfCard = readBoolean(ConfigKey.KEY_SHOW_BACK_OF_CARDS, this.showBackOfCard);
        this.bidHintsAll = readBoolean(ConfigKey.KEY_BID_HINTS_ALL, this.bidHintsAll);
        this.moveTrumps = readString(ConfigKey.KEY_MOVE_TRUMPS, this.moveTrumps) as MoveTrumps;
        this.showTricksAfterLead = readBoolean(ConfigKey.KEY_SHOW_TRICKS_AFTER_LEAD, this.showTricksAfterLead);
        this.showTrickCounter = readBoolean(ConfigKey.KEY_SHOW_TRICKS_COUNTER, this.showTrickCounter);
        this.hideBiddingAfterLead = readBoolean(ConfigKey.KEY_HIDE_BIDDING_AFTER_LEAD, this.hideBiddingAfterLead);
        this.hideCardsDuringKibitz = readBoolean(ConfigKey.KEY_HIDE_CARDS_KIBITZING, this.hideCardsDuringKibitz);
        this.defaultPass = readBoolean(ConfigKey.KEY_DEFAULT_PASS, this.defaultPass);
        this.playByKeyboard = readBoolean(ConfigKey.KEY_PLAY_BY_KEYBOARD, this.playByKeyboard);
        this.autoEnterPBK = readBoolean(ConfigKey.KEY_AUTO_ENTER_PBK, this.autoEnterPBK);
        this.hintHowToPlay = readBoolean(ConfigKey.KEY_HINT_HOW_TO_PLAY, this.hintHowToPlay);
        this.hintPlayConventionCard = readBoolean(ConfigKey.KEY_HINT_PLAY_CONVENTION_CARD, this.hintPlayConventionCard);
        this._playBy = readString(ConfigKey.KEY_PLAY_BY, this._playBy) as PlayBy;
        this.lastPlayedScoringMethod = readNumber(ConfigKey.KEY_LAST_SCORINGMETHOD, this.lastPlayedScoringMethod);
        this.autoZoom = readBoolean(ConfigKey.KEY_AUTO_ZOOM, this.autoZoom);
        this.backgroundColourLM = readString(ConfigKey.KEY_BACKGROUND_COLOUR_LM, this.backgroundColourLM);
        this.backgroundColourDM = readString(ConfigKey.KEY_BACKGROUND_COLOUR_DM, this.backgroundColourDM);
        // Floor at the smallest size the options panel offers, or choosing it would
        // not survive a restart.
        this.fontSize = Math.max(12.0, readNumber(ConfigKey.KEY_FONT_SIZE, this.fontSize));
        this.fontBold = readBoolean(ConfigKey.KEY_FONT_BOLD, this.fontBold);
        this.fontWeight = readNumber(ConfigKey.KEY_FONT_WEIGHT, this.fontWeight);
        this.diagramMode = readBoolean(ConfigKey.KEY_DIAGRAM_MODE, this.diagramMode);
        this.animations = readBoolean(ConfigKey.KEY_ANIMATIONS, this.animations);
        this.singleWindow = readBoolean(ConfigKey.KEY_SINGLE_WINDOW, this.singleWindow);
        this.showActivitiesInLobby = readBoolean(ConfigKey.KEY_ACTIVITIES_IN_LOBBY, this.showActivitiesInLobby);
        this.showActivitiesWithTables = readBoolean(ConfigKey.KEY_ACTIVITIES_WITH_TABLES, this.showActivitiesWithTables);
        this.onTopCC = readBoolean(ConfigKey.KEY_ONTOP_CC, this.onTopCC);
        this.onTopContacts = readBoolean(ConfigKey.KEY_ONTOP_CONTACTS, this.onTopContacts);
        this.onTopBoardResults = readBoolean(ConfigKey.KEY_ONTOP_BOARD_RESULTS, this.onTopBoardResults);
        this.onTopCompetitionResults = readBoolean(ConfigKey.KEY_ONTOP_COMPETITION_RESULTS, this.onTopCompetitionResults);

        // urls stored in local storage (is overridden by server and config file)
        try {
            const urls = readString(ConfigKey.KEY_URL_LIST, "[]");
            this.url = JSON.parse(urls);
        } catch (e) {
            console.debug(`couldn't parse urls.`, e);
        }
        console.debug('read config', toRaw(this));
    }

    write() {
        // preferences
        writeKey(ConfigKey.KEY_VERSION, Version.getVersionString());
        writeKey(ConfigKey.KEY_REGISTRY_VERSION, REGISTRY_VERSION);
        // Stamped here too, so restoreDefaultSettings() - which clears localStorage and
        // writes a fresh Config - doesn't re-arm migrate() and undo a setting the user
        // deliberately picked after the reset.
        stampMigrationVersion();

        writeKey(ConfigKey.KEY_LANGUAGE, this.languageId);
        writeKey(ConfigKey.KEY_CARD_LANGUAGE, this.cardLanguageId);
        // writeKey(ConfigKey.KEY_LOGIN_ACCOUNT, StepBridge.user.getMapName()); // TODO not needed??
        writeKey(ConfigKey.KEY_SOUND_BID_TURN, this.soundAtBidTurn);
        writeKey(ConfigKey.KEY_SOUND_PLAY_TURN, this.soundAtPlayTurn);
        writeKey(ConfigKey.KEY_SOUND_PRIVATE_MSG, this.soundAtPrivateMessages);
        writeKey(ConfigKey.KEY_SOUND_MEMBER_ENTRY, this.soundAtMembersEntry);
        writeKey(ConfigKey.KEY_BIDDINGBOX_KIND, this.biddingBox);
        writeKey(ConfigKey.KEY_CARD_ORDER, this.cardOrder);
        writeKey(ConfigKey.KEY_CARD_FACE, this.cardFace);
        writeKey(ConfigKey.KEY_CARD_WIDE_SPACED, this.cardWideSpaced);
        writeKey(ConfigKey.KEY_CARD_IMAGES, this.cardImages);
        writeKey(ConfigKey.KEY_FOUR_COLOUR_DECK, this.fourColourDeck);
        writeKey(ConfigKey.KEY_SHOW_BACK_OF_CARDS, this.showBackOfCard);
        writeKey(ConfigKey.KEY_BID_HINTS_ALL, this.bidHintsAll);
        writeKey(ConfigKey.KEY_MOVE_TRUMPS, this.moveTrumps);
        writeKey(ConfigKey.KEY_SHOW_TRICKS_AFTER_LEAD, this.showTricksAfterLead);
        writeKey(ConfigKey.KEY_SHOW_TRICKS_COUNTER, this.showTrickCounter);
        writeKey(ConfigKey.KEY_HIDE_BIDDING_AFTER_LEAD, this.hideBiddingAfterLead);
        writeKey(ConfigKey.KEY_HIDE_CARDS_KIBITZING, this.hideCardsDuringKibitz);
        writeKey(ConfigKey.KEY_DEFAULT_PASS, this.defaultPass);
        writeKey(ConfigKey.KEY_AUTO_SHOW_CHAT, this.autoShowChat);
        writeKey(ConfigKey.KEY_AUTO_SHOW_BOARD_RESULTS, this.autoShowBoardResult);
        writeKey(ConfigKey.KEY_BACKGROUND_COLOUR_LM, this.backgroundColourLM);
        writeKey(ConfigKey.KEY_BACKGROUND_COLOUR_DM, this.backgroundColourDM);
        writeKey(ConfigKey.KEY_PLAY_BY_KEYBOARD, this.playByKeyboard);
        writeKey(ConfigKey.KEY_AUTO_ENTER_PBK, this.autoEnterPBK);
        writeKey(ConfigKey.KEY_HINT_HOW_TO_PLAY, this.hintHowToPlay);
        writeKey(ConfigKey.KEY_HINT_PLAY_CONVENTION_CARD, this.hintPlayConventionCard);
        writeKey(ConfigKey.KEY_PLAY_BY, this._playBy);
        writeKey(ConfigKey.KEY_LAST_SCORINGMETHOD, this.lastPlayedScoringMethod);
        writeKey(ConfigKey.KEY_AUTO_ZOOM, this.autoZoom);
        writeKey(ConfigKey.KEY_FONT_SIZE, this.fontSize);
        writeKey(ConfigKey.KEY_FONT_BOLD, this.fontBold);
        writeKey(ConfigKey.KEY_FONT_WEIGHT, this.fontWeight);
        writeKey(ConfigKey.KEY_DIAGRAM_MODE, this.diagramMode);
        writeKey(ConfigKey.KEY_ANIMATIONS, this.animations);
        writeKey(ConfigKey.KEY_SINGLE_WINDOW, this.singleWindow);
        writeKey(ConfigKey.KEY_ACTIVITIES_IN_LOBBY, this.showActivitiesInLobby);
        writeKey(ConfigKey.KEY_ACTIVITIES_WITH_TABLES, this.showActivitiesWithTables);
        writeKey(ConfigKey.KEY_ONTOP_CC, this.onTopCC);
        writeKey(ConfigKey.KEY_ONTOP_CONTACTS, this.onTopContacts);
        writeKey(ConfigKey.KEY_ONTOP_BOARD_RESULTS, this.onTopBoardResults);
        writeKey(ConfigKey.KEY_ONTOP_COMPETITION_RESULTS, this.onTopCompetitionResults);

        writeKey(ConfigKey.KEY_URL_LIST, JSON.stringify(this.url));
    }

    localizeClub() {
        this.dateFormat = "ddd D-M-YYYY";
        this.timeFormat = "HH:mm";
    }

    /**
     * This will read the server side config, like game server url and club id
     */
    async loadFile(cf: ConfigFile) {
        return new Promise((resolve, reject) => {

            // Load the config file like club-nl.conf
            cf.load().then(() => {

                console.debug("Read " + cf.url, cf.map);

                const gameServer = useGameServer();

                gameServer.clearHosts();
                // read hosts urls from config file, like 
                // gameserver1=wss://gamenl2.stepbridge.nl:2002/
                // gameserver2=wss://gamenl1.stepbridge.nl:2002/
                for (let i = 1; i < 10; i++) {
                    const url = cf.get(ConfigKey.KEY_GAMESERVER + i);

                    if (url == null) {
                        break;
                    }
                    gameServer.addHost(url);
                }

                // get the api base
                const api = cf.get(ConfigKey.KEY_APISERVER + "1");
                // and remove slash at the end.
                if (api) {
                    gameServer.setApi(api.replace(/\/$/, ''));
                }

                const server = cf.get(ConfigKey.KEY_SERVER);
                const lang = cf.get(ConfigKey.KEY_LANGUAGE);

                if (server !== undefined) {
                    switch (server.toLowerCase()) {
                        case "nl":
                            gameServer.setNL();;
                            this.languageId = LanguageId.LANGUAGE_DUTCH;
                            break;
                        case "au":
                            gameServer.setAU();
                            this.languageId = LanguageId.LANGUAGE_ENGLISH;
                            break;
                        case "uk":
                            gameServer.setUK();
                            this.languageId = LanguageId.LANGUAGE_ENGLISH;
                            break;
                        default:
                            reject("Unknown server " + server + " in config.");
                            return;
                    }
                } else {
                    reject("No server entry in config.");
                    return;
                }

                if (lang != null) {
                    switch (lang.toLowerCase()) {
                        case "nl":
                            this.languageId = LanguageId.LANGUAGE_DUTCH;
                            break;
                        case "en":
                            this.languageId = LanguageId.LANGUAGE_ENGLISH;
                            break;
                        default:
                            reject("Unknown language " + lang + " in config.");
                            return;
                    }
                }
                this.cardLanguageId = this.languageId;

                const language = ['', 'english', 'dutch', 'german'];
                console.log('server language is', language[this.languageId]);
                // overwrite language
                this.languageId = readNumber(ConfigKey.KEY_LANGUAGE, this.languageId);
                this.cardLanguageId = readNumber(ConfigKey.KEY_CARD_LANGUAGE, this.cardLanguageId);
                console.log('config language is', language[this.languageId]);

                // set some urls
                this.url.forgotPW = cf.get(ConfigKey.KEY_URL_FORGOT_PW);
                this.url.reactivation = cf.get(ConfigKey.KEY_URL_REACTIVATION);

                this.cardSvgServer = cf.get(ConfigKey.KEY_CARD_SVG)?.toLowerCase() !== 'false';

                this.localizeClub();

                // final callback
                resolve(this);
            }).catch((reason) => {
                reject(reason);
            });

        });
    }
}
